import React from "react";
import StandingsTable from "../core/StandingsTable";
import DivisionInfo from "./DivisionInfo";
import Bracket from "./PlayoffBracket";
import DivisionWinner from "./DivisionWinner";
import { useQuery } from "@tanstack/react-query";
import Loading from "../core/Loading";
import GameFeaturedPlayers from "../game/GameFeaturedPlayers";
import GamePlayerSpotlight from "../game/GamePlayerSpotlight";
import GameEvent from "../game/GameEvent";

const renderPlayerSpotlight = (playerData) => {
  if (playerData !== undefined) {
    return (
        <GamePlayerSpotlight
            id={playerData.id}
            club={playerData.club}
            title={playerData.title}
            name={playerData.name}
            team_name={playerData.team_name}
            backup={playerData.team_img}
            desc={playerData.description}
            stat={playerData.value}
        />
    );
  }
  return null;
};

const DivisionStandings = ({ divisionData }) => {

  const processData = (jsonData) => {
    let processedData = {
      stats: [],
      title: jsonData.blip,
      next_game: jsonData.next_game,
      watch: [],
      vet: [],
      net: [],
      form: [],
      featured_players: jsonData.featured_players,
    };

    try {
      processedData.watch =
          jsonData.featured_players.watch[
              Math.floor(Math.random() * jsonData.featured_players.watch.length)
              ];
      processedData.vet =
          jsonData.featured_players.vet[
              Math.floor(Math.random() * jsonData.featured_players.vet.length)
              ];
      processedData.net =
          jsonData.featured_players.net[
              Math.floor(Math.random() * jsonData.featured_players.net.length)
              ];
      processedData.form =
          jsonData.featured_players.form[
              Math.floor(Math.random() * jsonData.featured_players.form.length)
              ];

    } catch (e) {
      console.error(e);
    }

    try {
      jsonData.standings.forEach((record) => {
        let ejected = false;

        record.penalties.forEach((penalty) => {
          if (penalty.ejected === true) {
            ejected = true;
          }
        });

        processedData.stats.push({
          footnotes: new Set(),
          logo: record.logo.lg,
          team_name: record.name,
          games: record.games,
          record: `${record.wins}-${record.losses}-${record.draws}`,
          points: record.points,
          gd: `${record.gf > record.ga ? "+" : ""}${record.gf - record.ga}`,
          gd_sort: record.gf - record.ga,
          w: record.wins,
          d: record.draws,
          l: record.losses,
          gf: record.gf,
          ga: record.ga,
          team_class: `${ejected ? "strikethrough" : ""}`,
          club: record.club,
          penalties: record.penalties
        });
      });

      processedData.stats.sort(function (a, b) {
        return (
          b.points - a.points ||
          b.gd_sort - a.gd_sort ||
          a.games - b.games ||
          b.w - a.w
        );
      });
    } catch (e) {
      console.error(e);
    }
    return processedData;
  };
  
  const { isLoading, error, data } = useQuery({
    staleTime: Infinity,
    queryKey: ["division", divisionData.id, "standings"],
    queryFn: () =>
      fetch(
        `${process.env.REACT_APP_BUIHA_API_URL}/division/${divisionData.id}/standings/`
      )
        .then((res) => res.json())
        .then((json) => {
          return processData(json);
        }),
  });

  if (isLoading) return <Loading />;
  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      <div className="flex flex-col items-start justify-center lg:justify-between mb-3 w-full">

        <div className="w-full flex flex-col items-start justify-start">

          {/* Leader and next game component */}
          {data.stats.length > 0 && data.next_game !== null ? (
              <DivisionInfo
                  leader={data.stats[0].team_name}
                  leaderimg={data.stats[0].logo}
                  leader_record={data.stats[0].record}
                  team1={data.next_game.home_team.blip}
                  team2={data.next_game.away_team.blip}
                  team1img={data.next_game.home_team.logo.lg}
                  team2img={data.next_game.away_team.logo.lg}
                  epocdate={data.next_game.date}
                  location={data.next_game.location}
              />
          ) : (
              data.stats.length > 0 && (
                  <DivisionWinner
                      champions={{'cup_name': data.stats[0].team_name, 'cup_img': data.stats[0].logo}}
                      nationals={divisionData.name.includes("Nationals")}
                      title={divisionData.name + " " + divisionData.season}
                  />
              )
          )}

            <GameEvent eventName={"Standings"} title={true} />

            <StandingsTable table_data={data} expanded={true} division_id={divisionData.id} />

          <GameEvent eventName={"Team Leaders"} title={true} />

          <div className="flex flex-wrap xs:flex-row w-full xs:mt-4 mt-2 pb-2 justify-center items-center">
            {renderPlayerSpotlight(data.watch)}
            {renderPlayerSpotlight(data.vet)}
            {renderPlayerSpotlight(data.net)}
            {renderPlayerSpotlight(data.form)}

            {(!data.watch && !data.vet && !data.net && !data.form) && (
                <p className="text-center w-full rounded py-2 dark:bg-dark-4 bg-light-2">Preview not available</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DivisionStandings;
